import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { DRBFMCasePrecheckItem, PrecheckAnswerStatus } from '../../../types/drbfm';

interface DRBFMPrecheckStepProps {
  items: DRBFMCasePrecheckItem[];
  isLoading: boolean;
  onChange: (items: DRBFMCasePrecheckItem[]) => void;
  onSubmit: (items: DRBFMCasePrecheckItem[]) => void | Promise<unknown>;
  onBack: () => void;
}

const DRBFMPrecheckStep: React.FC<DRBFMPrecheckStepProps> = ({
  items,
  isLoading,
  onChange,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const statusLabels: Record<PrecheckAnswerStatus, string> = {
    unanswered: t('drbfm.case.answerStatus.unanswered', '未回答'),
    answered: t('drbfm.case.answerStatus.answered', '回答済み'),
    unknown: t('drbfm.case.answerStatus.unknown', '不明'),
    not_applicable: t('drbfm.case.answerStatus.not_applicable', '該当なし'),
  };

  const updateItem = (
    index: number,
    update: Pick<DRBFMCasePrecheckItem, 'answerStatus' | 'answerText'>,
  ) => {
    onChange(items.map((item, currentIndex) => (
      currentIndex === index ? { ...item, ...update } : item
    )));
  };

  const handleAnswerTextChange = (index: number, value: string) => {
    const trimmed = value.trim();
    updateItem(index, {
      answerStatus: trimmed ? 'answered' : 'unanswered',
      answerText: value,
    });
  };

  const handleStatusClick = (index: number, answerStatus: 'unknown' | 'not_applicable') => {
    updateItem(index, {
      answerStatus,
      answerText: null,
    });
  };

  const handleSubmit = async () => {
    await onSubmit(items);
  };
  const hasUnansweredItems = items.some((item) => item.answerStatus === 'unanswered');
  const isSubmitDisabled = isLoading || hasUnansweredItems;

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('drbfm.case.precheckTitle', '事前確認')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('drbfm.case.precheckDescription', '不明点を明示してからDRBFM分析へ進みます。')}
        </Typography>
      </Box>

      {items.length === 0 ? (
        <Typography color="text.secondary">
          {t('drbfm.case.noPrecheckItems', '事前確認項目はありません。')}
        </Typography>
      ) : (
        <Stack spacing={2.5} divider={<Divider flexItem />}>
          {items.map((item, index) => (
            <Stack key={item.id || index} spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" component="h3">
                  {item.question}
                </Typography>
                <Chip
                  label={statusLabels[item.answerStatus]}
                  color={item.answerStatus === 'answered' ? 'success' : 'default'}
                  size="small"
                  variant={item.answerStatus === 'unanswered' ? 'outlined' : 'filled'}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                {item.reason}
              </Typography>

              <TextField
                label={t('drbfm.case.answer', '回答')}
                value={item.answerText || ''}
                onChange={(event) => handleAnswerTextChange(index, event.target.value)}
                disabled={isLoading}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText={t('drbfm.case.answerHelper', '入力すると回答済みになります')}
              />

              <Stack direction="row" spacing={1}>
                <Button
                  variant={item.answerStatus === 'unknown' ? 'contained' : 'outlined'}
                  onClick={() => handleStatusClick(index, 'unknown')}
                  disabled={isLoading}
                >
                  {t('drbfm.case.answerStatus.unknown', '不明')}
                </Button>
                <Button
                  variant={item.answerStatus === 'not_applicable' ? 'contained' : 'outlined'}
                  onClick={() => handleStatusClick(index, 'not_applicable')}
                  disabled={isLoading}
                >
                  {t('drbfm.case.answerStatus.not_applicable', '該当なし')}
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          disabled={isLoading}
          onClick={onBack}
        >
          {t('drbfm.case.backToChangeReview', '変更点レビューに戻る')}
        </Button>
        <Button
          variant="contained"
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
          }
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {isLoading
            ? t('drbfm.case.generatingDrbfm', 'DRBFM生成中')
            : t('drbfm.case.generateDrbfm', 'DRBFMを生成')}
        </Button>
      </Box>

      {hasUnansweredItems && (
        <Typography variant="body2" color="error">
          {t('drbfm.case.answerRequired', 'すべての確認項目に回答するか、不明または該当なしを選択してください。')}
        </Typography>
      )}
    </Stack>
  );
};

export default DRBFMPrecheckStep;
